import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "countInput",
    "countInputText",
    "decrementButton",
    "hiddenInput",
    "incrementButton"
  ]
  declare readonly countInputTarget: HTMLInputElement
  declare readonly countInputTextTarget: HTMLSpanElement
  declare readonly decrementButtonTarget: HTMLButtonElement
  declare readonly hiddenInputTargets: HTMLInputElement[]
  declare readonly incrementButtonTarget: HTMLButtonElement

  static values = {
    available: Number,
    count: Number,
    max: Number,
    min: Number,
    occupancy: Number,
    price: Number,
    step: Number,
    roomId: Number,
    roomName: String
  }
  declare availableValue: number
  declare countValue: number
  declare maxValue: number
  declare readonly minValue: number
  declare readonly occupancyValue: number
  declare readonly priceValue: number
  declare readonly stepValue: number
  declare readonly roomIdValue: number
  declare readonly roomNameValue: string

  // Every time this value changes we dispatch countChanged that in turn triggers
  // roomSelected on the package bookings controller.
  // That will always call setMax and we only set the disabled/enabled state
  // on decrement/increment button there.
  countValueChanged(newValue: number, oldValue: number): void {
    this.countInputTarget.value = this.countValue.toString()

    this.enableHiddenInputs()
    this.setInputText()
    this.dispatch("countChanged", {
      detail: { delta: newValue - oldValue, name: this.countInputTarget.name }
    })
  }

  decrement(): void {
    this.countValue = Math.max(this.minValue, this.countValue - this.stepValue)
  }

  increment(): void {
    this.countValue = Math.min(this.maxValue, this.countValue + this.stepValue)
  }

  get count(): number {
    return +this.countInputTarget.value
  }

  get name(): string {
    return this.countInputTarget.name
  }

  get selectedPax(): number {
    return this.countValue * this.occupancyValue
  }

  get selectedPrice(): number {
    return this.count * this.priceValue
  }

  // Called by package booking controller
  setAvailable(delta: number): void {
    this.availableValue = Math.max(0, this.availableValue - delta)
  }

  // Called by package booking controller
  setMax(remainingPax: number): void {
    this.maxValue =
      this.countValue +
      Math.min(this.availableValue, Math.floor(remainingPax / this.occupancyValue))
    this.countInputTarget.value = Math.min(this.countValue, this.maxValue).toString()

    this.decrementButtonTarget.setAttribute(
      "aria-disabled",
      this.countValue - this.stepValue < this.minValue ? "true" : "false"
    )
    this.incrementButtonTarget.setAttribute(
      "aria-disabled",
      this.countValue + this.stepValue > this.maxValue ? "true" : "false"
    )
  }

  private enableHiddenInputs(): void {
    // Disabled inputs aren't submitted, so disable all those beyond the current count
    this.hiddenInputTargets.forEach((input, index) => (input.disabled = index >= this.countValue))
  }

  private setInputText(): void {
    this.countInputTextTarget.textContent = this.countValue.toString()
  }
}
